/**
 * EverythingInOneSittingSection — homepage reassurance strip that pins
 * down exactly what the ₹999 / $49 plan delivers, placed right below
 * the pricing card so the buyer sees the full bundle at a glance
 * before hitting the FAQ.
 */
import React from 'react';
import { motion } from 'framer-motion';
import {
  FileEdit,
  PenLine,
  LineChart,
  FileText,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';

const ITEMS = [
  {
    icon: FileEdit,
    title: 'LinkedIn Profile Rewrite',
    body: 'Up to 3 review rounds with rewritten Headline, About, Experience & Skills sections.',
    tint: 'from-indigo-500/15 to-transparent',
    ring: 'ring-indigo-200/60',
    accent: 'text-indigo-600',
    testid: 'sitting-item-rewrite',
  },
  {
    icon: PenLine,
    title: '20 Thought-Leadership Posts',
    body: 'Ready-to-publish LinkedIn posts drafted in your voice — trending, POV, prompt-led and research-backed formats.',
    tint: 'from-rose-500/15 to-transparent',
    ring: 'ring-rose-200/60',
    accent: 'text-rose-600',
    testid: 'sitting-item-posts',
  },
  {
    icon: LineChart,
    title: 'Peer Benchmark Deep-Dive',
    body: 'See how you stack up against the leaders in your space — positioning gaps, white-space niches and content cadence.',
    tint: 'from-emerald-500/15 to-transparent',
    ring: 'ring-emerald-200/60',
    accent: 'text-emerald-600',
    testid: 'sitting-item-peer',
  },
  {
    icon: FileText,
    title: 'Recruiter-Ready Resume',
    body: 'A premium resume makeover auto-built from your audit — design and download included, every missing metric pre-highlighted for you to fill.',
    tint: 'from-amber-500/15 to-transparent',
    ring: 'ring-amber-200/60',
    accent: 'text-amber-600',
    testid: 'sitting-item-resume',
  },
];

export function EverythingInOneSittingSection({ onPrimaryCta }) {
  const navigate = useNavigate();
  const handleCta = () => {
    if (typeof onPrimaryCta === 'function') return onPrimaryCta();
    navigate('/signup');
  };

  return (
    <section
      data-testid="everything-in-one-sitting"
      className="relative py-20 sm:py-24"
    >
      {/* subtle grain + gradient wash to separate from pricing above */}
      <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-white via-slate-50/60 to-white dark:from-slate-950 dark:via-slate-900/40 dark:to-slate-950" />

      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="mb-10 sm:mb-14 max-w-2xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-3">
            One evening. Zero fluff.
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-tight">
            Everything you need <span className="text-indigo-600 dark:text-indigo-400">in one sitting</span>.
          </h2>
          <p className="mt-4 text-base text-muted-foreground">
            Four deliverables land in your dashboard the moment your audit finishes — no add-ons, no surprises.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={it.testid}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={it.testid}
                className={`group relative rounded-2xl bg-white/80 dark:bg-slate-900/60 backdrop-blur-sm ring-1 ${it.ring} p-5 sm:p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-16px_rgba(15,23,42,0.08)] hover:shadow-[0_1px_2px_rgba(15,23,42,0.04),0_20px_40px_-20px_rgba(79,70,229,0.18)] transition-all duration-300`}
              >
                <div className={`absolute inset-0 -z-10 rounded-2xl bg-gradient-to-br ${it.tint} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className="flex items-start gap-4">
                  <div className={`shrink-0 flex h-11 w-11 items-center justify-center rounded-xl bg-white dark:bg-slate-800 ring-1 ${it.ring} ${it.accent}`}>
                    <Icon size={20} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold tracking-tight">{it.title}</h3>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-muted-foreground">
                      {it.body}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
          <Button
            size="lg"
            className="rounded-full h-11 px-6"
            onClick={handleCta}
            data-testid="everything-sitting-cta"
          >
            Start my makeover
            <ArrowRight size={16} className="ml-1.5" />
          </Button>
          <span className="text-[13px] text-muted-foreground">
            ₹999 / $49 · one-time · valid for 15 days
          </span>
        </div>
      </div>
    </section>
  );
}

export default EverythingInOneSittingSection;
